import React from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import {
  HiddenAlertsSection,
  HiddenAlertsSectionProps,
} from '@/experiences/sales-portal/domain/insights/components/hidden-alerts-section';
import { HiddenAlertsSectionSkeleton } from '@/experiences/sales-portal/domain/insights/components/hidden-alerts-section-skeleton';
import { useAlertStatusMutator } from '@/experiences/sales-portal/domain/insights/blocs/mutators/use-alert-status-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

interface HiddenAlertsContainerProps {
  activeAlertsLoading: boolean;
  alerts: Alert[];
  error: Error | null;
  loading: boolean;
  renderSection?: (props: HiddenAlertsSectionProps) => React.ReactNode;
  supplierUUID: string;
}

export const HiddenAlertsContainer: React.FC<HiddenAlertsContainerProps> = ({
  activeAlertsLoading,
  alerts,
  error,
  loading,
  renderSection,
  supplierUUID,
}) => {
  const { showErrorToast } = useExperienceToasts();

  const {
    visibleAlerts,
    restoreAlert,
    deleteAlert,
    clearAlerts,
    loading: clearing,
  } = useAlertStatusMutator(alerts);

  if (loading || activeAlertsLoading) {
    return <HiddenAlertsSectionSkeleton />;
  }

  if (error || visibleAlerts.length === 0) {
    return null;
  }

  const handleRestore = (alert: Alert) => {
    void restoreAlert(alert).catch(() => showErrorToast());
  };

  const handleDelete = (alert: Alert) => {
    void deleteAlert(alert).catch(() => showErrorToast());
  };

  const handleClearAll = () => {
    void clearAlerts(visibleAlerts).catch(() => showErrorToast());
  };

  const sectionProps: HiddenAlertsSectionProps = {
    alerts: visibleAlerts,
    clearing,
    onClearAll: handleClearAll,
    onDelete: handleDelete,
    onRestore: handleRestore,
    supplierUUID,
  };

  return (
    <>
      {renderSection ? (
        renderSection(sectionProps)
      ) : (
        <HiddenAlertsSection {...sectionProps} />
      )}
    </>
  );
};
